import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getSupabase, supabaseSchema } from '../../components/supabaseClient';
import { getSignedFilesUrl } from '../../utils/signedUrls';
import { buildReportsFolder } from '../../utils/reportsPath';
import { bucketName_files, getBackendURL } from '../../components/env';
import Uploader from '../../components/upload/Uploader';
import MedStudentAIAssistant from './MedStudentAIAssistant';
import AIChatPane from './agents/AIChatPane';
import AgentsPane from './agents/AgentsPane';
import { canAccessComponent } from '../../config/roles';
import ProcessDiffReview from './process/ProcessDiffReview';
import ProcessSourceList from './process/ProcessSourceList';
import { groupReportsByClassification } from './process/reportCategories';
import {
  copilotResolvedEhrHint,
  extractTranscriptPlainTextForClinicalAi,
  fetchCopilotSnapshotForAudioPublicUrl,
  fetchCopilotSnapshotAtBucketRelativePath,
  generateClinicalTabsAndPersistCopilotSnapshot,
} from '../AudioRecorder/utils/copilotBackupUtils';
import { clinicalTabValueToPlain } from '../../utils/clinicalTabFormatting';
import { isPatientUpload, patientUploadLabel } from '../../utils/uploadProvenance';
import CopilotUpgradeButtons from '../../components/CopilotUpgradeButtons';
import { pickAudioDisplayName } from '../../components/table/audio/AudioPlayerModal';
import { filterAudiosForVisitDate, transcriptBelongsToAudio } from '../../utils/audioMetadata';

// Right-side panel that hosts the AI Assistant, Reports list, and Live
// Transcript stream as switchable tabs. Mirrors the three-tab layout from
// the doctor PatientPage but styled to match the medstudent clinic page
// (Tailwind / slate palette / no Chakra). The panel itself is collapsible —
// when `collapsed` is true the parent renders the rail instead.

// Each tab carries its own accent so the right panel reads as colorful
// rather than a single blue strip. Colours line up with the IconRail
// palette: blue=notes/AI, amber=reports, emerald=live (matches the live
// recording dot already used in LiveTranscriptTab).
const STATIC_TABS = [
  {
    key: 'ai',
    label: 'AI Chat',
    icon: '✨',
    activeCls: 'border-blue-500 text-blue-600 dark:text-blue-300 bg-blue-50/60 dark:bg-blue-500/[0.10]',
    idleCls:   'text-blue-500/80 dark:text-blue-400/80 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50/40 dark:hover:bg-blue-500/[0.06]',
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: '📁',
    activeCls: 'border-amber-500 text-amber-600 dark:text-amber-300 bg-amber-50/60 dark:bg-amber-500/[0.10]',
    idleCls:   'text-amber-500/80 dark:text-amber-400/80 hover:text-amber-600 dark:hover:text-amber-300 hover:bg-amber-50/40 dark:hover:bg-amber-500/[0.06]',
  },
  {
    key: 'transcript',
    label: 'Live Transcript',
    icon: '🎙️',
    activeCls: 'border-emerald-500 text-emerald-600 dark:text-emerald-300 bg-emerald-50/60 dark:bg-emerald-500/[0.10]',
    idleCls:   'text-emerald-500/80 dark:text-emerald-400/80 hover:text-emerald-600 dark:hover:text-emerald-300 hover:bg-emerald-50/40 dark:hover:bg-emerald-500/[0.06]',
  },
  {
    key: 'copilot',
    label: 'Copilot',
    icon: '🤖',
    activeCls: 'border-purple-500 text-purple-600 dark:text-purple-300 bg-purple-50/60 dark:bg-purple-500/[0.10]',
    idleCls:   'text-purple-500/80 dark:text-purple-400/80 hover:text-purple-600 dark:hover:text-purple-300 hover:bg-purple-50/40 dark:hover:bg-purple-500/[0.06]',
  },
];

// Review tab is conditional — only renders when the chart has parked a
// Process diff via the "Park in sidebar" button. Indigo accent so it
// reads as "action required" against the other four tabs (which are all
// blue/amber/emerald/purple). The badge (pending-edit count) is rendered
// inside the label by the parent so this config only owns colors.
const REVIEW_TAB = {
  key: 'review',
  label: 'Review',
  icon: '📝',
  activeCls: 'border-indigo-500 text-indigo-600 dark:text-indigo-300 bg-indigo-50/60 dark:bg-indigo-500/[0.10]',
  idleCls:   'text-indigo-500/80 dark:text-indigo-400/80 hover:text-indigo-600 dark:hover:text-indigo-300 hover:bg-indigo-50/40 dark:hover:bg-indigo-500/[0.06]',
};

function bytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExt(name) {
  const i = (name || '').lastIndexOf('.');
  return i === -1 ? '' : name.substring(i + 1).toLowerCase();
}

// Non-empty extracted-metadata fields (ignoring internal bookkeeping keys).
function metadataEntries(meta) {
  if (!meta || typeof meta !== 'object') return [];
  return Object.entries(meta).filter(
    ([k, v]) =>
      k !== 'truncation_info' &&
      v !== null &&
      v !== '' &&
      !(Array.isArray(v) && v.length === 0),
  );
}

// Primary chip label — the most identifying field available, matching the
// ResultsTable modal's precedence.
function metadataLabel(meta) {
  if (!meta || typeof meta !== 'object') return '';
  return (
    meta.document_type ||
    meta.patient_name ||
    meta.diagnosis ||
    meta.title ||
    'Details'
  );
}

// One-line "field: value · field: value" summary for the hover tooltip.
function metadataSummary(meta) {
  return metadataEntries(meta)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${Array.isArray(v) ? v.join(', ') : v}`)
    .join(' · ');
}

// ── Reports tab ─────────────────────────────────────────────────────────────
// Lists files in `patient.reports` (supabase storage path) and previews the
// selected one inline. Same storage call shape as MedStudentReportsModal —
// kept inline here so the panel is self-contained.
function ReportsTab({ patient, onCollapse }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  // Bumped after an upload completes so the file-list effect re-lists the
  // folder and the freshly uploaded reports appear without a page reload.
  const [refreshKey, setRefreshKey] = useState(0);
  // Files currently uploading / being OCR-processed in the background (the
  // upload modal closes immediately in quiet mode). Drives the inline
  // "Processing…" indicator; each entry clears when its file finishes.
  const [processing, setProcessing] = useState({});
  const handleProcessingChange = useCallback((filePath, info) => {
    setProcessing((prev) => {
      const next = { ...prev };
      if (info) next[filePath] = info.message || 'Processing…';
      else delete next[filePath];
      return next;
    });
    // A file finishing (info === null) means it's stored + classified — re-list
    // so it appears under the right classification group.
    if (!info) setRefreshKey((k) => k + 1);
  }, []);
  const processingCount = Object.keys(processing).length;
  // Expand-to-overlay state: when true, the active preview renders as a
  // fixed full-screen card over the clinic page instead of inline in the
  // sidebar. Always resets to false when a new file is opened or the
  // preview is closed (so a fresh click never re-enters expanded mode by
  // surprise).
  const [isExpanded, setIsExpanded] = useState(false);
  // Fall back to the derived reports folder when `patient.reports` is blank
  // (it's only auto-populated by the fax match/assign flow).
  const folderPath = patient?.reports || buildReportsFolder(patient);
  // PDF preview ref + reload key — once the user manually zooms inside the
  // browser's PDF viewer, it remembers that zoom and ignores `#view=FitH`
  // on subsequent iframe resizes. To restore auto-fit after a resize, we
  // remount the iframe (changing its React `key` re-applies the FitH hash
  // at the new container width).
  //
  // We *only* remount when the mouse is released — earlier versions used a
  // 200ms ResizeObserver debounce, but the browser's PDF viewer flashes
  // black during reload and the debounce was firing mid-drag (any pause
  // > 200ms during the drag triggered a remount). Listening for `mouseup`
  // means at most ONE flash per drag, at the very end, after the user
  // settles on the new size.
  const previewWrapRef = useRef(null);
  const [previewKey, setPreviewKey] = useState(0);
  useEffect(() => {
    if (!previewing || previewing.ext !== 'pdf') return undefined;
    const el = previewWrapRef.current;
    if (!el) return undefined;
    let lastWidth = el.clientWidth;
    let widthAtLastMouseDown = lastWidth;
    const onMouseDown = () => { widthAtLastMouseDown = el.clientWidth; };
    const onMouseUp = () => {
      // Only remount if the container actually changed width since the
      // mousedown — filters out unrelated clicks (tab buttons, list rows,
      // PDF viewer controls). Small 4px tolerance for sub-pixel noise.
      const w = el.clientWidth;
      if (Math.abs(w - widthAtLastMouseDown) > 4) {
        lastWidth = w;
        setPreviewKey((k) => k + 1);
      }
    };
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [previewing]);

  // Whenever the user opens a different file (or clears the preview), drop
  // expanded mode so the next preview starts inline like the user expects.
  useEffect(() => {
    setIsExpanded(false);
  }, [previewing]);

  // Esc closes the overlay back to inline mode. Only attach the listener
  // while expanded so we're not eating Esc presses for the rest of the page.
  useEffect(() => {
    if (!isExpanded) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isExpanded]);

  // The PDF iframe needs a remount on each expand/collapse transition so the
  // browser's PDF viewer re-applies `#view=FitH` against the new container
  // width (otherwise it remembers the previous zoom). Same trick as the
  // splitter-resize effect above, just driven by the mode toggle instead of
  // a mouse drag.
  useEffect(() => {
    if (previewing?.ext === 'pdf') setPreviewKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  useEffect(() => {
    if (!folderPath) {
      setFiles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const sb = await getSupabase();
        const { data, error: listErr } = await sb.storage
          .from(bucketName_files || 'files')
          .list(folderPath);
        if (cancelled) return;
        if (listErr) throw listErr;
        const onlyFiles = (data || []).filter((d) => d.name && d.name.includes('.'));
        // Fetch each file's cached classification + extracted metadata (same
        // source the Process modal source picker and the ResultsTable modal
        // use) so the Reports panel can both sub-bucket files by Labs /
        // Imaging / Medical Records / Other AND surface the per-file document
        // type / extracted fields inline.
        const paths = onlyFiles.map((f) => `${folderPath}/${f.name}`);
        let cacheByPath = {};
        if (paths.length) {
          try {
            const { data: rows } = await sb
              .from('file_classification_cache')
              .select('file_path, classification, metadata, created_at, uploaded_by_name')
              .in('file_path', paths);
            for (const r of rows || []) cacheByPath[r.file_path] = r;
          } catch (_classErr) {
            // Classification cache is best-effort; missing rows just fall
            // through to "Other Documents" in the grouping helper.
          }
        }
        if (cancelled) return;
        const decorated = onlyFiles.map((f) => {
          // `f.metadata` from storage.list() holds size/mimetype — keep it and
          // stash the extracted document metadata under a distinct key.
          const cache = cacheByPath[`${folderPath}/${f.name}`] || {};
          return {
            ...f,
            classification: cache.classification || 'Other',
            docMetadata: cache.metadata || null,
            classifiedAt: cache.created_at || null,
            uploadedByName: cache.uploaded_by_name || null,
          };
        });
        setFiles(decorated);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [folderPath, refreshKey]);

  // Bucket files by classification for the sub-grouped render below. Mirrors
  // the Process Forms source picker exactly so the clinician sees the same
  // taxonomy in both surfaces.
  const groupedFiles = useMemo(() => groupReportsByClassification(files), [files]);

  // Resolve a public URL for the preview iframe / img. Same pattern doctor
  // PatientPage uses for inline-report previews.
  const openPreview = async (file) => {
    try {
      const fullPath = `${folderPath}/${file.name}`;
      // Signed URL so the preview works when `files` is a private bucket.
      const url = await getSignedFilesUrl(fullPath);
      setPreviewing({ name: file.name, ext: fileExt(file.name), url });
    } catch (err) {
      console.warn('[ReportsTab] preview url failed:', err.message);
    }
  };

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
      {!folderPath && (
        <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
          No reports folder linked to this visit.
        </div>
      )}
      {/* Upload bar — add one or many reports to this patient. Reuses the
          shared Uploader (same AI/OCR/DB pipeline as Schedules); on completion
          we bump refreshKey to re-list the folder. Hidden while a preview is
          open so it doesn't crowd the previewer header. */}
      {folderPath && !previewing && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800/60">
          {processingCount > 0 ? (
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
              <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing {processingCount} report{processingCount === 1 ? '' : 's'}…
            </div>
          ) : (
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {files.length} file{files.length === 1 ? '' : 's'}
            </div>
          )}
          <Uploader
            selection={patient}
            quiet
            onProcessingChange={handleProcessingChange}
            onUploadComplete={() => setRefreshKey((k) => k + 1)}
            renderTrigger={({ onClick, disabled }) => (
              <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                title={disabled ? 'Select a patient first' : 'Upload reports to this patient'}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-amber-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-amber-500 hover:bg-amber-400 hover:shadow-md text-white"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload
              </button>
            )}
          />
        </div>
      )}
      {folderPath && loading && (
        <div className="p-4 text-sm text-slate-500 dark:text-slate-400">Loading reports…</div>
      )}
      {folderPath && error && (
        <div className="p-4 text-sm text-red-600 dark:text-red-400">Couldn't load reports: {error}</div>
      )}
      {folderPath && !loading && !error && files.length === 0 && (
        <div className="p-4 text-sm text-slate-500 dark:text-slate-400">No files uploaded yet.</div>
      )}
      {/* File list — grouped by classification (Labs / Imaging / Medical
          Records / Other Documents). When there's only one bucket the header
          is still rendered so the user has a consistent visual structure
          across patients. */}
      {!previewing && files.length > 0 && (
        <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3 space-y-3">
          {groupedFiles.map((g) => (
            <div key={g.classification}>
              <div className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 flex items-center gap-2">
                <span>{g.label}</span>
                <span className="text-slate-400 dark:text-slate-500 font-normal normal-case tracking-normal">({g.items.length})</span>
              </div>
              <div className="space-y-1.5">
                {g.items.map((f) => {
                  const ext = fileExt(f.name);
                  return (
                    <button
                      key={f.name}
                      type="button"
                      onClick={() => openPreview(f)}
                      className="w-full text-left p-3 rounded-lg border border-slate-200 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 flex items-center gap-3"
                    >
                      <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold uppercase shrink-0">
                        {ext || '·'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{f.name}</div>
                        {/* Extracted document metadata — document-type chip
                            (hover for the full field list) + service date,
                            mirroring the ResultsTable modal. */}
                        {metadataEntries(f.docMetadata).length > 0 && (
                          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                            <span
                              className="inline-block max-w-full truncate rounded px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30"
                              title={metadataSummary(f.docMetadata)}
                            >
                              {metadataLabel(f.docMetadata)}
                            </span>
                            {f.docMetadata.date_of_service && (
                              <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                {f.docMetadata.date_of_service}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Patient uploads carry their own badge — the
                            clinician needs to distinguish a file the patient
                            sent from one staff uploaded or a fax assigned. */}
                        {isPatientUpload(f.docMetadata, f.uploadedByName) && (
                          <div className="mt-1">
                            <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/40">
                              ⬆ {patientUploadLabel(f.docMetadata, f.uploadedByName)}
                            </span>
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {bytes(f.metadata?.size)}
                          {f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString()}` : ''}
                          {f.uploadedByName && !isPatientUpload(f.docMetadata, f.uploadedByName)
                            ? ` · ${f.uploadedByName}`
                            : ''}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Inline preview header + body. When `isExpanded` is true we hide
          the inline body (the overlay below renders the same content full
          screen) but keep the header in place so collapsing back lands
          right where the user left off. */}
      {previewing && (
        <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800/60">
            {/* Filename takes the left; Back + Expand controls are pushed to
                the right edge so they line up with the expanded overlay's
                button cluster. */}
            <div className="flex-1 min-w-0 text-sm font-semibold text-slate-900 dark:text-slate-100 truncate" title={previewing.name}>
              {previewing.name}
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              title={isExpanded ? 'Collapse to sidebar' : 'Expand preview'}
              aria-label={isExpanded ? 'Collapse preview' : 'Expand preview'}
              className="shrink-0 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200 flex items-center justify-center"
            >
              {isExpanded ? (
                // Collapse icon — two arrows pointing inward toward center
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 20H4v-5M20 9h-5V4M4 20l6-6M20 4l-6 6" />
                </svg>
              ) : (
                // Expand icon — two arrows pointing outward to corners
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 9V4h5M20 15v5h-5M4 4l6 6M20 20l-6-6" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={() => setPreviewing(null)}
              className="shrink-0 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200"
            >
              ← Back
            </button>
          </div>
          {!isExpanded && (
            <div
              ref={previewWrapRef}
              className="flex-1 bg-slate-100 dark:bg-slate-900/40"
              style={{ minHeight: 0 }}
            >
              {renderPreviewBody(previewing, previewKey)}
            </div>
          )}
        </div>
      )}
      {/* Full-page overlay — only mounted when both a preview exists AND
          the user toggled expanded mode. The guard on `previewing` means
          the Expand button is itself only reachable when a file is open,
          so we never end up with an empty overlay. */}
      {previewing && isExpanded && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex flex-col">
          <div className="m-6 flex-1 rounded-2xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-2xl overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-800/60">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate flex-1" title={previewing.name}>
                {previewing.name}
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                title="Collapse to sidebar"
                aria-label="Collapse preview"
                className="text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200 flex items-center justify-center"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 20H4v-5M20 9h-5V4M4 20l6-6M20 4l-6 6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setPreviewing(null)}
                title="Close preview"
                aria-label="Close preview"
                className="text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200 flex items-center justify-center"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
                </svg>
              </button>
            </div>
            <div
              ref={previewWrapRef}
              className="flex-1 bg-slate-100 dark:bg-slate-900/40"
              style={{ minHeight: 0 }}
            >
              {renderPreviewBody(previewing, previewKey)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Renders the actual file body (PDF iframe / text iframe / image / fallback
// link). Pulled out of `ReportsTab` so the inline-sidebar layout and the
// expanded-overlay layout can share one source of truth — keeps the iframe
// `key` and file-type branching identical between both modes.
function renderPreviewBody(previewing, previewKey) {
  if (!previewing) return null;
  const { ext, url, name } = previewing;
  if (ext === 'pdf' && url) {
    // `#view=FitH` tells Chrome/Edge's built-in PDF viewer to scale the
    // page to the iframe width so the report zooms to fill the panel as
    // the user drags the splitter or toggles expanded mode. `#toolbar=0`
    // hides the viewer chrome so narrow panels don't waste vertical space.
    // `key={previewKey}` remounts the iframe after the panel settles at a
    // new size — reapplies FitH and overrides any manual zoom the user did
    // before the resize.
    return (
      <iframe
        key={previewKey}
        title={name}
        src={`${url}#view=FitH&toolbar=0`}
        className="w-full h-full border-0"
      />
    );
  }
  if (['csv', 'txt'].includes(ext) && url) {
    return <iframe title={name} src={url} className="w-full h-full border-0" />;
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext) && url) {
    return (
      <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
        <img src={url} alt={name} className="max-w-full max-h-full object-contain" />
      </div>
    );
  }
  return (
    <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
      Preview not available for .{ext}. <a className="underline text-blue-600 dark:text-blue-300" href={url} target="_blank" rel="noreferrer">Open in new tab</a>
    </div>
  );
}

// ── Live Transcript tab ────────────────────────────────────────────────────
// Subscribes to the same window-message + localStorage signals the doctor
// PatientPage uses for its Live Transcript panel, so the transcript stream
// from whichever recording surface is active (RecordingBubble, external
// recorder window, etc.) shows up here too.
function LiveTranscriptTab({ patient, visibleAudios = [], selectedAudioIndex = 0, setSelectedAudioIndex }) {
  const [text, setText] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.type === 'LIVE_TRANSCRIPT_UPDATE') {
        setText(event.data.text || '');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'liveTranscriptUpdate' && event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue);
          if (parsed?.text) setText(parsed.text);
        } catch (_) { /* ignore */ }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const onCustomEvent = (event) => {
      if (event.detail?.text != null) {
        setText(event.detail.text);
      }
    };
    window.addEventListener('liveTranscriptUpdate', onCustomEvent);
    return () => window.removeEventListener('liveTranscriptUpdate', onCustomEvent);
  }, []);

  // Auto-scroll as new transcript text streams in.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [text]);

  const activeAudio = visibleAudios[selectedAudioIndex];
  // Resolve the saved transcript for the currently-selected audio. Two passes:
  //   (1) exact match (session_id / audio_url / filename) — preferred
  //   (2) fallback to any non-recap transcript for the visit — covers the
  //       case where the audio row + transcript row were written with
  //       slightly different metadata (URL query strings, casing, missing
  //       session_id) and the strict match returns nothing. Showing nothing
  //       when a transcript plainly exists for the visit is the bug this
  //       fallback fixes.
  const allVisitTranscripts = Array.isArray(patient?.transcripts)
    ? patient.transcripts.filter((t) => !t.is_ai_recap)
    : [];
  const matchedTranscript = activeAudio && allVisitTranscripts.length > 0
    ? allVisitTranscripts.find((t) => transcriptBelongsToAudio(activeAudio, t)) || null
    : null;
  const transcriptForSelected = matchedTranscript
    ? extractTranscriptPlainTextForClinicalAi(matchedTranscript)
    : '';
  // Last-resort fallback: concatenate any remaining visit transcripts that
  // didn't attach to a more_audios row. Only used when there's no live text
  // and no per-audio transcript so we don't dump unrelated noise on top of
  // a real transcript.
  const fallbackVisitTranscripts = !matchedTranscript && allVisitTranscripts.length > 0
    ? allVisitTranscripts
        .map((t) => extractTranscriptPlainTextForClinicalAi(t))
        .filter(Boolean)
    : [];

  const selectedTranscript = transcriptForSelected
    || (fallbackVisitTranscripts.length > 0 ? fallbackVisitTranscripts.join('\n\n') : '');

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800/60 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
        <span className="text-[11px] font-bold tracking-widest text-slate-500 dark:text-slate-400">
          LIVE TRANSCRIPT
        </span>
      </div>

      {visibleAudios.length > 1 && (
        <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-800/60 flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase text-slate-500">Select Recording:</label>
          <select
            value={selectedAudioIndex}
            onChange={(e) => setSelectedAudioIndex(Number(e.target.value))}
            className="w-full text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-2 py-1.5 focus:outline-none"
          >
            {visibleAudios.map((aud, idx) => (
              <option key={idx} value={idx}>
                {pickAudioDisplayName(aud, idx + 1)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4">
        {text || selectedTranscript ? (
          <div className="text-sm leading-relaxed text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
            {text || selectedTranscript}
          </div>
        ) : (
          <div className="text-sm text-slate-400 dark:text-slate-500 italic">
            No transcription yet. Start a recording to see the live transcript here.
          </div>
        )}
      </div>
    </div>
  );
}

function CopilotTab({
  patient,
  patientId,
  scheduledDate,
  visibleAudios = [],
  selectedCopilotAudioIndex = 0,
  setSelectedCopilotAudioIndex,
}) {
  const [copilotPayload, setCopilotPayload] = useState(null);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [userCopilotEnabled, setUserCopilotEnabled] = useState(null);
  const [copilotGenerateLoading, setCopilotGenerateLoading] = useState(false);
  // Task #N (Leejoe 2026-06-28): Consider opens first by default — it's the
  // primary unprompted-AI surface (per [[project-universal-add-problem]] +
  // patient-profile compliance migration). A&P/Scores/Reports are
  // secondary. Future: drop A&P + Reports tabs entirely (per Leejoe).
  const [activeSubTab, setActiveSubTab] = useState('consider');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) {
          if (!cancelled) setUserCopilotEnabled(true);
          return;
        }
        const { data: userData } = await supabase
          .from("users")
          .select("copilot_enabled")
          .eq("id", user.id)
          .single();
        if (!cancelled) {
          // Stripe paywall removed (2026-06-25, Brett). Premium Copilot for everyone.
          setUserCopilotEnabled(true);
        }
      } catch {
        if (!cancelled) setUserCopilotEnabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const activeAudio = visibleAudios[selectedCopilotAudioIndex];
    if (
      !patientId ||
      !activeAudio ||
      !(activeAudio.url || activeAudio.audio_url) ||
      userCopilotEnabled !== true
    ) {
      setCopilotPayload(null);
      setCopilotLoading(false);
      return;
    }
    const pool =
      Array.isArray(patient?.transcripts) && patient.transcripts.length > 0
        ? patient.transcripts.filter((t) => !t.is_ai_recap)
        : [];
    const matched = pool.length > 0 && activeAudio ? pool.find((t) => transcriptBelongsToAudio(activeAudio, t)) : null;
    const resolvedCopilotEhr = copilotResolvedEhrHint(matched, activeAudio);

    let cancelled = false;
    let isAutogenerating = false;
    setCopilotLoading(true);
    setCopilotPayload(null);
    fetchCopilotSnapshotForAudioPublicUrl(
      activeAudio.url || activeAudio.audio_url,
      {
        resolvedCopilotEhr,
      }
    )
      .then((json) => {
        if (!cancelled) {
          if (json) {
            setCopilotPayload(json);
          } else {
            // Fallback: if there is no copilot tabs snapshot, but we have a transcript, generate it!
            const text = extractTranscriptPlainTextForClinicalAi(matched);
            if (text && text.trim()) {
              console.log("ℹ️ [Copilot] Fallback auto-generation triggered.");
              const baseUrl = getBackendURL();
              const audioPublicUrl = activeAudio.url || activeAudio.audio_url;
              const audioListIndexOneBased = selectedCopilotAudioIndex + 1;
              
              getSupabase().then(async (supabase) => {
                try {
                  const { data: { session } } = await supabase.auth.getSession();
                  const userId = session?.user?.id;
                  const userJwtToken = session?.access_token;
                  
                  const result = await generateClinicalTabsAndPersistCopilotSnapshot({
                    transcriptText: text,
                    backendBaseUrl: baseUrl,
                    audioRow: activeAudio,
                    transcriptRow: matched,
                    audioPublicUrl,
                    audioListIndexOneBased: audioListIndexOneBased >= 1 ? audioListIndexOneBased : undefined,
                    userId,
                    userJwtToken,
                  });
                  
                  if (!cancelled && result.ok) {
                    console.log("✅ [Copilot] Fallback auto-generation successful:", result);
                    const ehrHint = copilotResolvedEhrHint(matched, activeAudio);
                    let refreshed = null;
                    if (result.copilotRelativePath) {
                      const originMatch = audioPublicUrl.match(/^https?:\/\/[^/?#]+/i);
                      const storageOrigin = originMatch ? originMatch[0] : "";
                      refreshed = await fetchCopilotSnapshotAtBucketRelativePath(
                        result.copilotRelativePath,
                        storageOrigin
                      );
                    }
                    if (!refreshed) {
                      refreshed = await fetchCopilotSnapshotForAudioPublicUrl(audioPublicUrl, {
                        resolvedCopilotEhr: ehrHint,
                      });
                    }
                    if (!cancelled) {
                      if (result.clinical_tabs) {
                        setCopilotPayload({
                          ...(refreshed && typeof refreshed === "object" ? refreshed : {}),
                          created_at: new Date().toISOString(),
                          clinical_tabs: result.clinical_tabs,
                        });
                      } else if (refreshed) {
                        setCopilotPayload(refreshed);
                      }
                      window.dispatchEvent(new CustomEvent("copilotTabsGenerated"));
                    }
                  }
                } catch (err) {
                  console.error("❌ [Copilot] Fallback auto-generation error:", err);
                }
              });
            }
          }
        }
      })
      .finally(() => {
        if (!cancelled && !isAutogenerating) {
          setCopilotLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    patientId,
    visibleAudios,
    selectedCopilotAudioIndex,
    userCopilotEnabled,
    patient,
  ]);

  const handleGenerateCopilotTabs = async () => {
    const activeAudio = visibleAudios[selectedCopilotAudioIndex];
    const audioPublicUrl = activeAudio?.url || activeAudio?.audio_url || "";
    if (!audioPublicUrl.trim()) return;

    const pool =
      Array.isArray(patient?.transcripts) && patient.transcripts.length > 0
        ? patient.transcripts.filter((t) => !t.is_ai_recap)
        : [];
    const matched = pool.length > 0 && activeAudio ? pool.find((t) => transcriptBelongsToAudio(activeAudio, t)) : null;
    if (!matched) return;

    const text = extractTranscriptPlainTextForClinicalAi(matched);
    if (!text.trim()) return;

    const sb = await getSupabase();
    const baseUrl = getBackendURL();

    const audioListIndexOneBased = selectedCopilotAudioIndex + 1;

    setCopilotGenerateLoading(true);
    try {
      const { data: { session } } = await sb.auth.getSession();
      const userId = session?.user?.id;
      const userJwtToken = session?.access_token;

      const result = await generateClinicalTabsAndPersistCopilotSnapshot({
        transcriptText: text,
        backendBaseUrl: baseUrl,
        audioRow: activeAudio,
        transcriptRow: matched,
        audioPublicUrl,
        existingClinicalTabs: copilotPayload?.clinical_tabs ?? null,
        audioListIndexOneBased:
          audioListIndexOneBased >= 1 ? audioListIndexOneBased : undefined,
        userId,
        userJwtToken,
      });
      if (!result.ok) throw new Error(result.error);

      const originMatch = audioPublicUrl.match(/^https?:\/\/[^/?#]+/i);
      const storageOrigin = originMatch ? originMatch[0] : "";
      const ehrHint = copilotResolvedEhrHint(matched, activeAudio);

      let refreshed = null;
      if (result.copilotRelativePath) {
        refreshed = await fetchCopilotSnapshotAtBucketRelativePath(
          result.copilotRelativePath,
          storageOrigin
        );
      }
      if (!refreshed) {
        refreshed = await fetchCopilotSnapshotForAudioPublicUrl(audioPublicUrl, {
          resolvedCopilotEhr: ehrHint,
        });
      }

      if (result.clinical_tabs) {
        setCopilotPayload({
          ...(refreshed && typeof refreshed === "object" ? refreshed : {}),
          ...(copilotPayload && typeof copilotPayload === "object" ? copilotPayload : {}),
          created_at: new Date().toISOString(),
          clinical_tabs: result.clinical_tabs,
        });
      } else if (refreshed) {
        setCopilotPayload(refreshed);
      }
    } catch (err) {
      console.warn("Could not generate Copilot tabs:", err);
    } finally {
      setCopilotGenerateLoading(false);
    }
  };

  if (userCopilotEnabled === null) {
    return (
      <div className="p-4 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
        <span className="animate-spin text-slate-400 text-xs">🌀</span> Loading…
      </div>
    );
  }

  if (userCopilotEnabled === false) {
    return (
      <div className="p-4 flex flex-col gap-3">
        <p className="text-xs text-slate-700 dark:text-slate-300">
          Upgrade to premium to enable Copilot and view saved clinical tabs for this recording.
        </p>
        <CopilotUpgradeButtons />
      </div>
    );
  }

  if (visibleAudios.length === 0) {
    return (
      <div className="p-4 text-xs text-slate-500 dark:text-slate-400 italic">
        No audio recordings found for this visit.
      </div>
    );
  }

  const activeAudio = visibleAudios[selectedCopilotAudioIndex];
  const pool =
    Array.isArray(patient?.transcripts) && patient.transcripts.length > 0
      ? patient.transcripts.filter((t) => !t.is_ai_recap)
      : [];
  const matched = pool.length > 0 && activeAudio ? pool.find((t) => transcriptBelongsToAudio(activeAudio, t)) : null;
  const transcriptTextTrimmed = extractTranscriptPlainTextForClinicalAi(matched);
  const canGenerateOrRegenerate = !!transcriptTextTrimmed;

  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ minHeight: 0 }}>
      {visibleAudios.length > 1 && (
        <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-800/60 flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase text-slate-500">Select Recording:</label>
          <select
            value={selectedCopilotAudioIndex}
            onChange={(e) => setSelectedCopilotAudioIndex(Number(e.target.value))}
            className="w-full text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-2 py-1.5 focus:outline-none"
          >
            {visibleAudios.map((aud, idx) => (
              <option key={idx} value={idx}>
                {pickAudioDisplayName(aud, idx + 1)}
              </option>
            ))}
          </select>
        </div>
      )}

      {copilotLoading || copilotGenerateLoading ? (
        <div className="p-4 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <span className="animate-spin text-slate-400">🌀</span> {copilotGenerateLoading ? "Generating Copilot tabs..." : "Loading Copilot snapshot…"}
        </div>
      ) : !copilotPayload ? (
        <div className="p-4 flex flex-col gap-3">
          <p className="text-xs text-slate-500 dark:text-slate-400 italic">
            No Copilot snapshot found for this recording yet.
          </p>
          {canGenerateOrRegenerate ? (
            <button
              type="button"
              onClick={handleGenerateCopilotTabs}
              disabled={copilotGenerateLoading}
              className="px-3 py-1.5 text-[11px] font-semibold bg-purple-600 hover:bg-purple-500 disabled:bg-purple-400 text-white rounded self-start shadow-sm"
            >
              {copilotGenerateLoading ? 'Generating…' : 'Generate Copilot tabs from transcript'}
            </button>
          ) : (
            <p className="text-[11px] text-slate-500">
              Link a transcript to this audio to generate Copilot tabs.
            </p>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="px-4 py-1.5 bg-slate-50/50 dark:bg-slate-900/30 border-b border-slate-200 dark:border-slate-800/60 flex items-center justify-between gap-2">
            {copilotPayload.created_at && (
              <span className="text-[10px] text-slate-400">
                Saved: {new Date(copilotPayload.created_at).toLocaleString()}
              </span>
            )}
            {canGenerateOrRegenerate && (
              <button
                type="button"
                onClick={handleGenerateCopilotTabs}
                disabled={copilotGenerateLoading}
                className="text-[10px] text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800/80 rounded px-2 py-0.5 hover:bg-purple-50 dark:hover:bg-purple-950/20"
              >
                {copilotGenerateLoading ? 'Regenerating…' : 'Regenerate'}
              </button>
            )}
          </div>

          {/* Subtabs rendering */}
          {(() => {
            const ct = copilotPayload.clinical_tabs;
            const sections = [];
            const pushPlainSection = (id, label, raw, { alwaysShow = false } = {}) => {
              const val = clinicalTabValueToPlain(raw);
              const text =
                val && String(val).trim()
                  ? String(val).trim()
                  : alwaysShow
                    ? "Not discussed"
                    : "";
              if (!text) return;

              let contentNode;
              if (id === "consider" && text !== "Not discussed") {
                const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
                let itemsToUse = [];
                lines.forEach(line => {
                  if (line.match(/^[-*•\d.]/)) {
                    itemsToUse.push(line);
                  } else {
                    const sentences = line.match(/[^.!?]+[.!?]+(\s|$)/g) || [line];
                    sentences.forEach(s => {
                      const trimmed = s.trim();
                      if (trimmed) itemsToUse.push(trimmed);
                    });
                  }
                });

                const bulletItems = [];
                itemsToUse.forEach(item => {
                  const clean = item.replace(/^[\s-*•\d.)]+/, '').trim();
                  if (clean) {
                    bulletItems.push(clean);
                  }
                });

                if (bulletItems.length > 0) {
                  contentNode = (
                    <ul className="list-disc pl-4 space-y-1 text-slate-800 dark:text-slate-200 text-xs">
                      {bulletItems.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  );
                } else {
                  contentNode = (
                    <p className="text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap word-break">{text}</p>
                  );
                }
              } else {
                contentNode = (
                  <p className={`text-xs whitespace-pre-wrap word-break ${text === "Not discussed" ? "text-slate-400 italic" : "text-slate-800 dark:text-slate-200"}`}>
                    {text}
                  </p>
                );
              }

              sections.push({
                id,
                label,
                node: (
                  <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-lg border border-slate-100 dark:border-slate-800/40 overflow-y-auto max-h-[300px]">
                    {contentNode}
                  </div>
                ),
              });
            };

            if (ct && typeof ct === "object") {
              const evidence = ct.evidence;
              const evidenceHasContent =
                evidence != null &&
                evidence !== "" &&
                !(Array.isArray(evidence) && evidence.length === 0);

              let assessmentPlain = "";
              if (ct.assessment != null && ct.assessment !== "") {
                const v = clinicalTabValueToPlain(ct.assessment);
                if (v && String(v).trim()) assessmentPlain = String(v);
              }
              if (!assessmentPlain) assessmentPlain = "Not discussed";

              let planPlain = "";
              if (ct.plan != null && ct.plan !== "") {
                const v = clinicalTabValueToPlain(ct.plan);
                if (v && String(v).trim()) planPlain = String(v);
              }
              if (!planPlain) planPlain = "Not discussed";

              // Task #226 (Leejoe 2026-06-28): A&P tab DROPPED from Copilot.
              // Lives in the main note editor — no need to mirror here. The
              // assessment/plan/evidence values are still computed above so
              // any downstream consumer keeps working; we just don't render
              // the panel tab.
              void assessmentPlain; void planPlain; void evidence; void evidenceHasContent;

              pushPlainSection("consider", "Consider", ct.consider, { alwaysShow: true });

              const scores = ct.scores;
              const SCORES_EMPTY_PHRASES = ['none mentioned', 'none', 'not mentioned', 'not discussed', 'n/a'];
              const scoresIsEmpty =
                scores == null ||
                scores === '' ||
                (typeof scores === 'string' && SCORES_EMPTY_PHRASES.indexOf(scores.trim().toLowerCase()) !== -1) ||
                (Array.isArray(scores) && scores.length === 0);

              let scoresNode;
              if (scoresIsEmpty) {
                scoresNode = <p className="text-xs text-slate-400 italic">None mentioned</p>;
              } else {
                let lines = [];
                if (typeof scores === 'string') {
                  lines = scores.split(/\n|\\n/).map(l => l.trim()).filter(Boolean);
                } else if (Array.isArray(scores)) {
                  lines = scores.map(s => typeof s === 'object' ? JSON.stringify(s) : String(s).trim()).filter(Boolean);
                } else if (typeof scores === 'object') {
                  lines = [JSON.stringify(scores, null, 2)];
                } else {
                  lines = [String(scores)];
                }

                scoresNode = (
                  <ul className="list-disc pl-4 space-y-1 text-xs text-slate-800 dark:text-slate-200">
                    {lines.map((line, idx) => {
                      const colonIdx = line.indexOf(':');
                      if (colonIdx > -1) {
                        let label = line.substring(0, colonIdx).trim().replace(/^[-*•\d+.\s]+/, '');
                        const val = line.substring(colonIdx + 1).trim();
                        return (
                          <li key={idx} className="leading-relaxed">
                            <strong className="text-slate-700 dark:text-slate-300 font-semibold">{label}:</strong> {val}
                          </li>
                        );
                      }
                      const cleanedLine = line.replace(/^[-*•\d+.\s]+/, '');
                      return (
                        <li key={idx} className="leading-relaxed">
                          {cleanedLine}
                        </li>
                      );
                    })}
                  </ul>
                );
              }

              sections.push({
                id: "scores",
                label: "Scores",
                node: (
                  <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-lg border border-slate-100 dark:border-slate-800/40 overflow-y-auto max-h-[300px]">
                    {scoresNode}
                  </div>
                ),
              });

              // Task #226: Reports tab DROPPED from Copilot. Lives in the
              // dedicated Reports panel — no need to mirror here.
              // pushPlainSection("report_plain", "Report", ct.report_plain ?? ct.report, { alwaysShow: true });

              if (Array.isArray(ct.facts) && ct.facts.length > 0) {
                sections.push({
                  id: "facts",
                  label: `Facts (${ct.facts.length})`,
                  node: (
                    <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-lg border border-slate-100 dark:border-slate-800/40 overflow-y-auto max-h-[300px] space-y-1.5">
                      {ct.facts.map((fact, i) => (
                        <p key={i} className="text-xs text-slate-800 dark:text-slate-200">
                          {i + 1}. {fact.text || (typeof fact === "string" ? fact : JSON.stringify(fact))}
                          {fact.group && (
                            <span className="ml-1 bg-slate-250 dark:bg-slate-750 px-1 text-[9px] rounded text-slate-600 dark:text-slate-400 uppercase">
                              {fact.group}
                            </span>
                          )}
                        </p>
                      ))}
                    </div>
                  ),
                });
              }

              if (ct.soap_notes && typeof ct.soap_notes === "object" && Object.keys(ct.soap_notes).length > 0) {
                const sn = ct.soap_notes;
                if (sn.transcript_only || sn.facts_only || sn.combined) {
                  sections.push({
                    id: "soap_notes",
                    label: "SOAP",
                    node: (
                      <div className="bg-slate-50 dark:bg-slate-900/40 p-3 rounded-lg border border-slate-100 dark:border-slate-800/40 overflow-y-auto max-h-[300px] space-y-2.5">
                        {sn.transcript_only && (
                          <div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase">Transcript-based</span>
                            <p className="text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{sn.transcript_only}</p>
                          </div>
                        )}
                        {sn.facts_only && (
                          <div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase">Facts-based</span>
                            <p className="text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{sn.facts_only}</p>
                          </div>
                        )}
                        {sn.combined && (
                          <div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase">Combined</span>
                            <p className="text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{sn.combined}</p>
                          </div>
                        )}
                      </div>
                    ),
                  });
                }
              }
            }

            if (sections.length === 0) {
              return (
                <div className="p-4 text-xs text-slate-500 italic">
                  Copilot snapshot has no clinical sections.
                </div>
              );
            }

            return (
              <div className="flex-1 flex flex-col min-h-0 px-4 py-3" style={{ minHeight: 0 }}>
                {/* Horizontal tabs buttons */}
                <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-800/60 pb-2 mb-3">
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setActiveSubTab(s.id)}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors ${
                        activeSubTab === s.id
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400'
                          : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/40'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                {/* Active sub-tab content */}
                <div className="flex-1 overflow-y-auto min-h-0">
                  {sections.find((s) => s.id === activeSubTab)?.node}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ── Parked Review tab ──────────────────────────────────────────────────────
// Hosts ProcessDiffReview inline inside the right panel. Lifted out of
// ProcessModal when the user clicks "Park in sidebar" so the diff stays
// accessible without a floating modal. The header above the diff shows
// summary counts and a Dismiss button; the rest is identical UX to the
// in-modal review screen.
//
// Supports two phases:
//   - phase === 'sources': shows ProcessSourceList (re-opened inline with
//     the parked selection state). A "Generate diff" button transitions
//     the parked payload to phase === 'review'.
//   - phase === 'review':  shows ProcessDiffReview with the AI's edits.
function ParkedReviewTab({
  parkedReview,
  parkedApplying,
  parkedApproved,
  parkedPending,
  onApprove,
  onDeny,
  onEditSave,
  onApproveRest,
  onRejectAllClose,
  onApply,
  onDismiss,
  onUnpark,
  onToggleItem,
  onToggleGroup,
  onToggleExpand,
  onToggleTemplate,
  onResetSelection,
  onSelectOnlyNew,
  onClearAll,
  onGenerate,
  sourcesLoading,
  // Per-user chat "skill". The parked source picker generates through
  // suggest-edits, which honors the skill too, so it carries the same picker
  // the Process modal does.
  activeSkill = null,
  skillIntensity = 'full',
  onSkillChange = null,
  // Generation state forwarded from parent — see comment on
  // MedStudentRightPanel's `isGenerating` prop. The parked source
  // picker drives its Generate button + GeneratingTimer off these so
  // park↔unpark never resets the live clock.
  isGenerating = false,
  generationStartedAt = null,
}) {
  const phase = parkedReview?.phase || 'review';
  const totalEdits = (parkedReview?.edits || []).length;
  const deniedCount = Object.values(parkedReview?.approvals || {}).filter((s) => s === 'denied').length;
  const everyDecided = totalEdits > 0 && parkedPending === 0;
  const selectedCount = (parkedReview?.selectedIds || []).length;
  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ minHeight: 0 }}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800/60">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold tracking-widest text-indigo-700 dark:text-indigo-300">
            {phase === 'sources' ? 'PARKED · PICKING SOURCES' : phase === 'master' ? 'PARKED · MASTER NOTE' : 'PARKED REVIEW'}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {phase === 'sources' ? (
              <>{selectedCount} source{selectedCount === 1 ? '' : 's'} selected · click <span className="font-semibold">Generate diff</span> when ready</>
            ) : phase === 'master' ? (
              parkedReview?.masterGenerating && !parkedReview?.masterNoteOutput
                ? <>Generating master note… · it will flip to <span className="font-semibold">ready</span> here</>
                : <>Master Note ready · <span className="font-semibold">Unpark</span> to review &amp; accept</>
            ) : (
              <>{totalEdits} edit{totalEdits === 1 ? '' : 's'} ·{' '}
                <span className="text-emerald-600 dark:text-emerald-300 font-semibold">{parkedApproved} approved</span>
                {' · '}
                <span className="text-rose-600 dark:text-rose-300 font-semibold">{deniedCount} rejected</span>
                {' · '}
                <span className="text-indigo-600 dark:text-indigo-300 font-semibold">{parkedPending} pending</span>
              </>
            )}
          </div>
        </div>
        {typeof onDismiss === 'function' && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss parked review"
            title="Dismiss the parked review without applying or marking sources reviewed"
            className="shrink-0 text-[11px] px-2 py-1 rounded border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40"
          >
            Dismiss
          </button>
        )}
        {/* Unpark — opposite direction of "Park in sidebar" inside the
            Process modal. Hands the parked state back to the modal so
            the user can keep working in the floating review/diff. The
            right-bar tab disappears once the parent clears parkedReview;
            the modal re-opens with its selection / edits restored.
            Always rendered (no conditional) so the affordance is
            discoverable from the parked Review tab even if the parent's
            handler hasn't propagated yet — clicking with no handler is
            a safe no-op. */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof onUnpark === 'function') onUnpark();
          }}
          aria-label="Unpark — open in modal"
          title="Open this diff back in the Process modal"
          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded border border-indigo-300 bg-white dark:bg-indigo-950/20 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 8H5m14 0l-3-3m3 3l-3 3M5 16h14m-14 0l3 3m-3-3l3-3" />
          </svg>
          Unpark
        </button>
      </div>
      <div className="flex-1 min-h-0 p-3 overflow-hidden">
        {phase === 'sources' ? (
          <div className="h-full flex flex-col overflow-hidden">
            {sourcesLoading ? (
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400">Loading sources…</div>
            ) : (
              <div className="flex-1 min-h-0 overflow-auto">
                <ProcessSourceList
                  groups={parkedReview?.sourceGroups || []}
                  selectedIds={new Set(parkedReview?.selectedIds || [])}
                  expanded={new Set(parkedReview?.expanded || [])}
                  useTemplate={!!parkedReview?.useTemplate}
                  onToggleItem={onToggleItem}
                  onToggleGroup={onToggleGroup}
                  onToggleExpand={onToggleExpand}
                  onToggleTemplate={onToggleTemplate}
                  onResetSelection={onResetSelection}
                  onSelectOnlyNew={onSelectOnlyNew}
                  onClearAll={onClearAll}
                  onGenerate={onGenerate}
                  generating={isGenerating}
                  generationStartedAt={generationStartedAt}
                  activeSkill={activeSkill}
                  skillIntensity={skillIntensity}
                  onSkillChange={onSkillChange}
                />
              </div>
            )}
          </div>
        ) : phase === 'master' ? (
          parkedReview?.masterGenerating && !parkedReview?.masterNoteOutput ? (
            /* Placeholder while the master fetch is still running — the
               completion handler overwrites this payload with the ready
               note, flipping the tab automatically. */
            <div className="h-full flex items-center justify-center rounded border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50/40 dark:bg-indigo-950/10 p-4 text-sm text-indigo-700 dark:text-indigo-300">
              <span className="animate-pulse">Generating master note… safe to keep working.</span>
            </div>
          ) : (
            /* Master Note parked — read-only preview of the proposed whole-note
               rewrite. Accept lives in the modal; Unpark to get there. */
            <div className="h-full overflow-auto rounded border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/40 dark:bg-emerald-950/10 p-3 text-[12px] leading-relaxed whitespace-pre-wrap text-slate-700 dark:text-slate-200">
              {parkedReview?.masterNoteOutput || ''}
            </div>
          )
        ) : (
          <>
            <ProcessDiffReview
              text={parkedReview?.documentText || ''}
              locatedEdits={parkedReview?.edits || []}
              approvals={parkedReview?.approvals || {}}
              onApprove={onApprove}
              onDeny={onDeny}
              onEditSave={onEditSave}
              onApproveRest={onApproveRest}
              onRejectAllClose={onRejectAllClose}
              onApply={onApply}
              applying={parkedApplying}
            />
            {!everyDecided && parkedApproved === 0 && parkedPending > 0 && (
              <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 text-center">
                Approve or reject each suggestion, then click <span className="font-semibold">Apply to note</span> to write the changes.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Pop-out AI Chat ─────────────────────────────────────────────────────────
// State + geometry for the detachable AI Chat window. Persisted to
// localStorage so the user's position/size survives reloads, but the
// "popped" flag defaults to false on first load (the docked sidebar is
// the safer landing state).
const POPOUT_STORAGE_KEY = 'medstudent-ai-popout';
const POPOUT_MIN_W = 320;
const POPOUT_MIN_H = 320;
const POPOUT_DEFAULT = { popped: false, x: 120, y: 96, w: 480, h: 620 };

function readPopoutState() {
  try {
    const raw = typeof localStorage !== 'undefined'
      ? localStorage.getItem(POPOUT_STORAGE_KEY) : null;
    if (!raw) return POPOUT_DEFAULT;
    const parsed = JSON.parse(raw);
    return {
      ...POPOUT_DEFAULT,
      ...parsed,
      // Re-clamp on load in case viewport shrank between sessions.
      w: Math.max(POPOUT_MIN_W, Number(parsed.w) || POPOUT_DEFAULT.w),
      h: Math.max(POPOUT_MIN_H, Number(parsed.h) || POPOUT_DEFAULT.h),
    };
  } catch {
    return POPOUT_DEFAULT;
  }
}

// Floating wrapper for the AI Chat. The header acts as the drag handle;
// the bottom-right grip resizes. Position/size live in parent state so
// they survive remounts and can be persisted to storage.
function FloatingAI({
  x, y, w, h,
  onDragStart, onResizeStart, onDock, onClose,
  children,
}) {
  return (
    <div
      className="fixed z-[60] rounded-2xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-2xl flex flex-col overflow-hidden"
      style={{ left: x, top: y, width: w, height: h }}
      role="dialog"
      aria-label="AI Chat (popped out)"
    >
      {/* Drag handle — anywhere in the header bar starts a drag */}
      <div
        onMouseDown={onDragStart}
        className="cursor-move select-none flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800/60 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/[0.08] dark:to-indigo-500/[0.08]"
      >
        <svg className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" fill="currentColor" viewBox="0 0 16 16" aria-hidden>
          <circle cx="3" cy="3" r="1.2" /><circle cx="8" cy="3" r="1.2" /><circle cx="13" cy="3" r="1.2" />
          <circle cx="3" cy="8" r="1.2" /><circle cx="8" cy="8" r="1.2" /><circle cx="13" cy="8" r="1.2" />
          <circle cx="3" cy="13" r="1.2" /><circle cx="8" cy="13" r="1.2" /><circle cx="13" cy="13" r="1.2" />
        </svg>
        <span className="text-[11px] font-bold tracking-widest text-blue-700 dark:text-blue-200 uppercase">
          AI Chat
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onDock}
            title="Dock back into the sidebar"
            aria-label="Dock back"
            className="w-6 h-6 rounded flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/40"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close (returns to sidebar)"
            aria-label="Close"
            className="w-6 h-6 rounded flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/20 dark:hover:text-rose-300"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body — the AI Assistant fills the remaining space. */}
      <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
        {children}
      </div>

      {/* Corner resize grips — invisible 14px squares stretched into the
          corners. Each one calls `onResizeStart(e, dir)` with its
          direction so the parent's mousemove handler knows which edges
          to move. The bottom-right grip also shows the classic diagonal
          marks so the user has an obvious visual cue that the window is
          resizable. */}
      <button
        type="button"
        onMouseDown={(e) => onResizeStart(e, 'nw')}
        aria-label="Resize from top-left"
        title="Resize"
        tabIndex={-1}
        className="absolute top-0 left-0 w-3.5 h-3.5 cursor-nw-resize"
        style={{ touchAction: 'none' }}
      />
      <button
        type="button"
        onMouseDown={(e) => onResizeStart(e, 'ne')}
        aria-label="Resize from top-right"
        title="Resize"
        tabIndex={-1}
        className="absolute top-0 right-0 w-3.5 h-3.5 cursor-ne-resize"
        style={{ touchAction: 'none' }}
      />
      <button
        type="button"
        onMouseDown={(e) => onResizeStart(e, 'sw')}
        aria-label="Resize from bottom-left"
        title="Resize"
        tabIndex={-1}
        className="absolute bottom-0 left-0 w-3.5 h-3.5 cursor-sw-resize"
        style={{ touchAction: 'none' }}
      />
      <button
        type="button"
        onMouseDown={(e) => onResizeStart(e, 'se')}
        aria-label="Resize from bottom-right"
        title="Drag to resize"
        tabIndex={-1}
        className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
        style={{ touchAction: 'none' }}
      >
        <svg viewBox="0 0 16 16" className="w-full h-full" fill="currentColor" aria-hidden>
          <path d="M11 14 L14 14 L14 11 Z" />
          <path d="M6 14 L14 6 L14 8 L8 14 Z" opacity="0.6" />
          <path d="M11 14 L14 11 L14 13 L13 14 Z" opacity="0.4" />
        </svg>
      </button>
    </div>
  );
}

// A notes-c visit id. An agent's "This patient" must be a notes-c visit (the server looks the chart
// up there by this uuid), so other charts (roster charts, the combined day view) aren't offered.
const VISIT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ── Main panel ──────────────────────────────────────────────────────────────
export default function MedStudentRightPanel({
  patient,
  patientId,
  // Postgres schema of `patient` (cross-schema providers). Forwarded to the AI
  // assistant so its threads/history/query/pin-toggle hit the patient's real
  // tenant. Undefined for callers that don't set it → assistant uses its default.
  schema,
  documentContent,
  /** Latest plain text from live Quill — used when `visit.document` is null under collab. */
  documentPlainRef = null,
  userRole,
  scheduledDate,
  userInitials,
  onCollapse,
  hidden = false,
  // Panel width in px. Parent owns the state (so ResizeSplitter can drive it
  // + persist to localStorage). Default 416 ≈ the old `w-[26rem]`.
  width = 416,
  // Active tab is controlled — the parent owns it so external triggers (the
  // floating AI bubble button, future keyboard shortcuts, etc.) can switch
  // tabs from outside this component. Falls back to local state when the
  // parent doesn't supply the controlled props (single-source-of-truth
  // pattern: uncontrolled mode only kicks in if `activeTab` is `undefined`).
  activeTab: activeTabProp,
  onTabChange,
  // Prompt-injection signal forwarded to the AI assistant. Shape:
  // `{ text, sources, nonce }` — a bumped nonce triggers one auto-send.
  injectPrompt = null,
  onSettingsSaved,
  // One-shot `{ activeSkill, skillIntensity, nonce }` signal forwarded to the
  // AI assistant when the Process Sources modal changes the shared per-user
  // skill preference — without it the chat's skill badge would lag that change
  // until a remount.
  skillSync = null,
  // Live value + writer for that same preference, used by the parked source
  // picker (the Process modal owns the other copy of this control).
  activeSkill = null,
  skillIntensity = 'full',
  onSkillChange = null,
  // Phase B (MVP) — Process Forms diff renders INTO this slot inside the
  // Copilot tab area when processDiffInPanelMode is true. Replaces the
  // floating modal that kept getting occluded by the external recorder
  // popup window. The ref is read by ProcessModal at portal time.
  processDiffPanelSlotRef = null,
  processDiffInPanelMode = false,
  // Parked Process review — when set, the right bar surfaces a "Review" tab
  // alongside AI Chat / Reports / Live Transcript / Copilot. Clicking it
  // renders the diff inline in the right panel. Cleared by the parent's
  // apply/reject handlers once the review is acted on.
  parkedReview = null,
  parkedApplying = false,
  onParkedApprove = null,
  onParkedDeny = null,
  onParkedEditSave = null,
  onParkedApproveRest = null,
  onParkedRejectAllClose = null,
  onParkedApply = null,
  onParkedDismiss = null,
  onParkedUnpark = null,
  // Source-picker handlers — used when the parked review is in phase='sources'.
  // All mirror the in-modal ProcessModal handlers; see MedStudentClinicPage
  // for the canonical implementations.
  onParkedToggleItem = null,
  onParkedToggleGroup = null,
  onParkedToggleExpand = null,
  onParkedToggleTemplate = null,
  onParkedResetSelection = null,
  onParkedSelectOnlyNew = null,
  onParkedClearAll = null,
  onParkedGenerate = null,
  sourcesLoading = false,
  // Generation state — forwarded from MedStudentClinicPage so the
  // right-bar source picker's Generate button + GeneratingTimer
  // behave identically to the modal's. When the same flow is parked
  // mid-fetch and the user unpark → re-park, the timer continues
  // ticking from the parent-owned start timestamp instead of resetting
  // to 0:00 on the surface transition.
  isGenerating = false,
  generationStartedAt = null,
  // Clinic agents (pages/medstudent/agents). `agentsState` is the page's useAgents() answer, which
  // the page hands over only for roles with roles.json clinicPage.agentsTab (null otherwise);
  // `userId` is the signed-in user, for the agents pane.
  userId = null,
  agentsState = null,
}) {
  const [localActiveTab, setLocalActiveTab] = useState('ai');
  const activeTab = activeTabProp ?? localActiveTab;
  const setActiveTab = onTabChange ?? setLocalActiveTab;

  const visibleAudios = useMemo(() => {
    return filterAudiosForVisitDate(
      patient?.more_audios || [],
      scheduledDate || '',
      supabaseSchema
    );
  }, [patient?.more_audios, scheduledDate]);

  const [selectedAudioIndex, setSelectedAudioIndex] = useState(0);

  // ── AI Chat pop-out state ────────────────────────────────────────────────
  // Loaded lazily from localStorage so the user's last position/size sticks
  // across reloads. The `popped` flag IS persisted too — so closing and
  // reopening the page returns the user to whichever state they left it in.
  const [aiPop, setAiPop] = useState(readPopoutState);
  useEffect(() => {
    try { localStorage.setItem(POPOUT_STORAGE_KEY, JSON.stringify(aiPop)); }
    catch (_) { /* fine */ }
  }, [aiPop]);

  // Drag + resize use a ref to capture the starting geometry on mousedown
  // so subsequent mousemoves don't re-read stale state. Releasing happens
  // on window-level mouseup so it still works if the cursor leaves the
  // floating window mid-drag.
  const dragOpRef = useRef(null);
  const onAiDragStart = useCallback((e) => {
    dragOpRef.current = {
      type: 'drag',
      startX: e.clientX, startY: e.clientY,
      origX: aiPop.x, origY: aiPop.y,
    };
    e.preventDefault();
  }, [aiPop.x, aiPop.y]);
  // `dir` is one of nw / ne / sw / se. Each corner shifts a different set
  // of geometry fields:
  //   se → grows width/height only
  //   sw → shifts x left + grows width, grows height
  //   ne → shifts y up + grows height, grows width
  //   nw → shifts both x and y, grows width and height
  const onAiResizeStart = useCallback((e, dir) => {
    dragOpRef.current = {
      type: 'resize',
      dir,
      startX: e.clientX, startY: e.clientY,
      origX: aiPop.x, origY: aiPop.y,
      origW: aiPop.w, origH: aiPop.h,
    };
    e.preventDefault();
    e.stopPropagation(); // resize grip is inside the wrapper; don't bubble into drag
  }, [aiPop.x, aiPop.y, aiPop.w, aiPop.h]);

  useEffect(() => {
    if (!aiPop.popped) return undefined;
    const onMove = (e) => {
      const op = dragOpRef.current;
      if (!op) return;
      if (op.type === 'drag') {
        const dx = e.clientX - op.startX;
        const dy = e.clientY - op.startY;
        // Clamp so the title bar can't be dragged completely off-screen.
        const maxX = Math.max(0, window.innerWidth - 80);
        const maxY = Math.max(0, window.innerHeight - 40);
        setAiPop((p) => ({
          ...p,
          x: Math.min(maxX, Math.max(-(p.w - 80), op.origX + dx)),
          y: Math.min(maxY, Math.max(0, op.origY + dy)),
        }));
      } else if (op.type === 'resize') {
        const dx = e.clientX - op.startX;
        const dy = e.clientY - op.startY;
        // Compute candidate values from the corner direction, then clamp:
        //   - width/height to >= POPOUT_MIN_*
        //   - top edge (y) and left edge (x) to >= 0 (don't push the
        //     window above / left of the viewport)
        //   - bottom-right corner to stay inside the viewport
        let { origX: nx, origY: ny, origW: nw, origH: nh } = op;
        const east = op.dir.includes('e');
        const west = op.dir.includes('w');
        const south = op.dir.includes('s');
        const north = op.dir.includes('n');
        if (east) nw = op.origW + dx;
        if (west) { nw = op.origW - dx; nx = op.origX + dx; }
        if (south) nh = op.origH + dy;
        if (north) { nh = op.origH - dy; ny = op.origY + dy; }
        // Honor the min-size constraint by clamping back BOTH the size
        // and the position (otherwise dragging the NW corner past the
        // limit would keep moving x/y while w/h stayed pinned).
        if (nw < POPOUT_MIN_W) {
          if (west) nx -= POPOUT_MIN_W - nw;
          nw = POPOUT_MIN_W;
        }
        if (nh < POPOUT_MIN_H) {
          if (north) ny -= POPOUT_MIN_H - nh;
          nh = POPOUT_MIN_H;
        }
        // Keep the top-left corner on-screen.
        if (nx < 0) { if (west) nw += nx; nx = 0; }
        if (ny < 0) { if (north) nh += ny; ny = 0; }
        // Keep the bottom-right corner from running off the viewport.
        const vw = window.innerWidth, vh = window.innerHeight;
        if (nx + nw > vw) nw = Math.max(POPOUT_MIN_W, vw - nx);
        if (ny + nh > vh) nh = Math.max(POPOUT_MIN_H, vh - ny);
        setAiPop((p) => ({ ...p, x: nx, y: ny, w: nw, h: nh }));
      }
    };
    const onUp = () => { dragOpRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [aiPop.popped]);

  const popAiOut = () => setAiPop((p) => ({ ...p, popped: true }));
  const dockAiBack = () => setAiPop((p) => ({ ...p, popped: false }));
  // When `hidden` is true the parent renders a collapse-rail instead, but we
  // KEEP this panel mounted with display:none so the AI assistant's chat
  // history, source-chip toggles, and active-thread selection survive a
  // collapse/expand cycle. Unmounting (the previous behaviour) wiped the
  // messages state and forced a fresh fetch on expand — which the user saw
  // as a brief empty-chat flicker before the history snapped back in.
  const chatNode = (
    <MedStudentAIAssistant
      patientId={patientId}
      patientData={patient}
      schema={schema}
      documentContent={documentContent}
      documentPlainRef={documentPlainRef}
      userRole={userRole}
      scheduledDate={scheduledDate}
      userInitials={userInitials}
      injectPrompt={injectPrompt}
      onSettingsSaved={onSettingsSaved}
      skillSync={skillSync}
    />
  );
  // Clinic agents: with them on, the same node carries the Chat | Agents bar, both docked and
  // popped out, and the AI Chat tab carries the unseen badge whichever tab is open. The role is
  // checked here too, so the badge never shows without the bar.
  const agentsOn = !!agentsState && canAccessComponent(userRole, 'clinicPage.agentsTab');
  const agentsUnseen = agentsOn && Number.isFinite(agentsState.unseen) && agentsState.unseen > 0
    ? agentsState.unseen
    : 0;
  // The pane offers the open chart as the new agent's "This patient" only when it is a visit
  // (roster charts on /patients/:uuid and the combined day view are not). The schema always goes:
  // the members search looks in it.
  const agentsPatient = patient && patient._source !== 'roster' && VISIT_ID.test(String(patient.id))
    ? patient
    : null;
  const aiAssistantNode = agentsOn ? (
    <AIChatPane
      userRole={userRole}
      unseen={agentsUnseen}
      chat={chatNode}
      agents={<AgentsPane agentsState={agentsState} userId={userId} patient={agentsPatient} schema={schema} />}
    />
  ) : chatNode;

  // Build the visible tabs at render time so the parked Review tab
  // surfaces/disappears with parkedReview. We compute the pending-edit
  // count here so the badge can show actionable items at a glance
  // (pending = total − approved − denied; mirrors ProcessDiffReview).
  const parkedApprovals = parkedReview?.approvals || {};
  const parkedEdits = parkedReview?.edits || [];
  const parkedApproved = Object.values(parkedApprovals).filter((s) => s === 'approved').length;
  const parkedDenied = Object.values(parkedApprovals).filter((s) => s === 'denied').length;
  const parkedPending = Math.max(0, parkedEdits.length - parkedApproved - parkedDenied);
  const tabs = parkedReview ? [...STATIC_TABS, REVIEW_TAB] : STATIC_TABS;

  return (
    <>
    <aside
      style={{ width: `${width}px` }}
      className={[
        'shrink-0 border-l border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] flex flex-col',
        hidden ? 'hidden' : '',
      ].join(' ')}
      aria-hidden={hidden}
    >
      {/* Tab bar + collapse button. flex-wrap so Copilot/Review drop to a
          second row on narrow panels instead of clipping off the edge
          (Leejoe 2026-07-09: "should they drop a row down like Process and
          Master Note did on the left side?"). */}
      <div className="border-b border-slate-200 dark:border-slate-800/60 flex flex-wrap items-stretch">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          const isReview = tab.key === 'review';
          // The agents badge rides on AI Chat. At the default width it would push the label onto
          // two lines, so that tab keeps to one line while the badge shows. The badge is small and
          // sits close to the label so the bar itself still fits on one row there (measured at
          // 416 px: counts up to 99; longer ones fall back to the bar's own wrap).
          const agentsBadge = tab.key === 'ai' && agentsUnseen > 0;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              aria-pressed={isActive}
              className={[
                'flex-1 px-2 py-2.5 text-[11px] font-semibold flex items-center justify-center gap-1.5 border-b-2 transition-colors',
                isActive ? tab.activeCls : `border-transparent ${tab.idleCls}`,
                agentsBadge ? 'whitespace-nowrap' : '',
              ].join(' ')}
            >
              <span aria-hidden>{tab.icon}</span>
              {tab.label}
              {/* Parked-review badge — shows pending edit count so the
                  user can see at a glance that the diff still needs
                  decisions. Only rendered on the Review tab. */}
              {isReview && parkedPending > 0 && (
                <span
                  className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-indigo-600 text-white"
                  aria-label={`${parkedPending} pending edits`}
                  title={`${parkedPending} edit${parkedPending === 1 ? '' : 's'} still need a decision`}
                >
                  {parkedPending}
                </span>
              )}
              {/* Unseen agent updates. role="img" makes its label part of the tab's name. */}
              {agentsBadge && (
                <span
                  role="img"
                  className="-ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-[3px] rounded-full text-[9px] font-bold leading-none bg-rose-600 dark:bg-rose-500 text-white"
                  aria-label={`${agentsUnseen} unseen agent ${agentsUnseen === 1 ? 'update' : 'updates'}`}
                >
                  {agentsUnseen > 99 ? '99+' : agentsUnseen}
                </span>
              )}
            </button>
          );
        })}
        {/* Pop AI Chat into a floating window. Only meaningful while the
            AI tab is active and not already popped — hidden otherwise so
            it doesn't clutter the toolbar. */}
        {activeTab === 'ai' && !aiPop.popped && (
          <button
            type="button"
            onClick={popAiOut}
            aria-label="Pop out AI Chat"
            title="Pop out AI Chat into a draggable window"
            className="px-2 text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 border-l border-slate-200 dark:border-slate-800/60"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 3h7v7m0-7L10 14M5 10v10a1 1 0 001 1h10" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Collapse panel"
          title="Collapse panel"
          className="px-3 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 border-l border-slate-200 dark:border-slate-800/60"
        >
          {/* Right-pointing chevron — collapses the panel to the rail on the right */}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Tab content. The AI Assistant is mounted in EXACTLY ONE place —
          either inline here, or in the FloatingAI window below. Toggling
          pop/dock currently re-mounts it (chat history is server-backed
          so this is acceptable) — if local state ever needs to persist
          across the toggle, we'd switch to a single mount-point shuttled
          via React Portal. */}
      {activeTab === 'ai' && !aiPop.popped && aiAssistantNode}
      {activeTab === 'ai' && aiPop.popped && (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-8 gap-3" style={{ minHeight: 0 }}>
          <div className="text-3xl" aria-hidden>🪟</div>
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            AI Chat is in a floating window
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 max-w-[260px]">
            Drag the title bar to move it, drag the bottom-right corner to resize, or dock it back here.
          </div>
          <button
            type="button"
            onClick={dockAiBack}
            className="mt-1 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white shadow-sm"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Dock back into sidebar
          </button>
        </div>
      )}
      {activeTab === 'reports' && <ReportsTab patient={patient} />}
      {activeTab === 'transcript' && (
        <LiveTranscriptTab
          patient={patient}
          visibleAudios={visibleAudios}
          selectedAudioIndex={selectedAudioIndex}
          setSelectedAudioIndex={setSelectedAudioIndex}
        />
      )}
      {activeTab === 'copilot' && (
        processDiffInPanelMode ? (
          <div
            ref={processDiffPanelSlotRef}
            className="relative flex-1 min-h-0 overflow-hidden"
            data-process-diff-slot=""
          />
        ) : (
          <CopilotTab
            patient={patient}
            patientId={patientId}
            scheduledDate={scheduledDate}
            visibleAudios={visibleAudios}
            selectedCopilotAudioIndex={selectedAudioIndex}
            setSelectedCopilotAudioIndex={setSelectedAudioIndex}
          />
        )
      )}
      {/* Parked Process review — diff rendered inline in the right bar so
          the user can keep editing the note while the diff waits. Same
          ProcessDiffReview component the floating review modal used, just
          hosted inside the panel. The header bar above the diff carries a
          "Dismiss" button so the user can clear the parked review without
          acting on it (e.g. they decided to re-Process from scratch). */}
      {activeTab === 'review' && parkedReview && (
        <ParkedReviewTab
          parkedReview={parkedReview}
          parkedApplying={parkedApplying}
          parkedApproved={parkedApproved}
          parkedPending={parkedPending}
          onApprove={onParkedApprove}
          onDeny={onParkedDeny}
          onEditSave={onParkedEditSave}
          onApproveRest={onParkedApproveRest}
          onRejectAllClose={onParkedRejectAllClose}
          onApply={onParkedApply}
          onDismiss={onParkedDismiss}
          onUnpark={onParkedUnpark}
          onToggleItem={onParkedToggleItem}
          onToggleGroup={onParkedToggleGroup}
          onToggleExpand={onParkedToggleExpand}
          onToggleTemplate={onParkedToggleTemplate}
          onResetSelection={onParkedResetSelection}
          onSelectOnlyNew={onParkedSelectOnlyNew}
          onClearAll={onParkedClearAll}
          onGenerate={onParkedGenerate}
          sourcesLoading={sourcesLoading}
          activeSkill={activeSkill}
          skillIntensity={skillIntensity}
          onSkillChange={onSkillChange}
          isGenerating={isGenerating}
          generationStartedAt={generationStartedAt}
        />
      )}
    </aside>
    {aiPop.popped && (
      <FloatingAI
        x={aiPop.x} y={aiPop.y} w={aiPop.w} h={aiPop.h}
        onDragStart={onAiDragStart}
        onResizeStart={onAiResizeStart}
        onDock={dockAiBack}
        onClose={dockAiBack}
      >
        {aiAssistantNode}
      </FloatingAI>
    )}
    </>
  );
}
